import { resolveTargetName } from './groupWatchRuns';
import {
  mockPulseNow,
  observedInWindow,
  severityRank,
} from './marketingOpsSelectors';
import { findingPayloadString } from './topicWatchAgents';
import { formatAgentType } from './watchTowerSelectors';

import type { WatchFinding, WatchRun, WatchTowerMockState } from './types';

export type RunAnalysisSpanDays = 7 | 30 | 90;

export type TimelineRange = [number, number];

export interface RunIssueCard {
  finding: WatchFinding,
  run: WatchRun,
  companyName: string,
  country: string,
  city: string,
  lat: number | null,
  lng: number | null,
  industry: string,
  agentType: string,
  agentLabel: string,
  targetName: string,
}

export interface IssueMapMarker {
  key: string,
  country: string,
  city: string,
  lat: number,
  lng: number,
  count: number,
  maxSeverity: string,
  cards: RunIssueCard[],
}

export type IssueAgeUnit = 'now' | 'minutes' | 'hours' | 'days';

const CITY_COORDS: Record<string, { lat: number, lng: number }> = {
  'AE|fujairah': { lat: 25.1288, lng: 56.3265 },
  'BE|antwerp': { lat: 51.2194, lng: 4.4025 },
  'BE|brussels': { lat: 50.8503, lng: 4.3517 },
  'BR|sao paulo': { lat: -23.5505, lng: -46.6333 },
  'DE|ludwigshafen': { lat: 49.4771, lng: 8.4353 },
  'DK|aarhus': { lat: 56.1629, lng: 10.2039 },
  'ES|valencia': { lat: 39.4699, lng: -0.3763 },
  'IE|cork': { lat: 51.8985, lng: -8.4756 },
  'KR|ulsan': { lat: 35.5384, lng: 129.3114 },
  'LT|vilnius': { lat: 54.6872, lng: 25.2797 },
  'NP|kathmandu': { lat: 27.7172, lng: 85.3240 },
  'PL|poznan': { lat: 52.4064, lng: 16.9252 },
  'UA|kyiv': { lat: 50.4501, lng: 30.5234 },
  'US|boston': { lat: 42.3601, lng: -71.0589 },
  'ZA|cape town': { lat: -33.9249, lng: 18.4241 },
};

const COUNTRY_COORDS: Record<string, { lat: number, lng: number }> = {
  AE: { lat: 23.4241, lng: 53.8478 },
  AU: { lat: -25.2744, lng: 133.7751 },
  BE: { lat: 50.5039, lng: 4.4699 },
  BR: { lat: -14.2350, lng: -51.9253 },
  CA: { lat: 56.1304, lng: -106.3468 },
  CH: { lat: 46.8182, lng: 8.2275 },
  DE: { lat: 51.1657, lng: 10.4515 },
  DK: { lat: 56.2639, lng: 9.5018 },
  EG: { lat: 26.8206, lng: 30.8025 },
  ES: { lat: 40.4637, lng: -3.7492 },
  FR: { lat: 46.2276, lng: 2.2137 },
  GB: { lat: 55.3781, lng: -3.4360 },
  IE: { lat: 53.1424, lng: -7.6921 },
  IR: { lat: 32.4279, lng: 53.6880 },
  IT: { lat: 41.8719, lng: 12.5674 },
  KE: { lat: -0.0236, lng: 37.9062 },
  KR: { lat: 35.9078, lng: 127.7669 },
  LT: { lat: 55.1694, lng: 23.8813 },
  NG: { lat: 9.0820, lng: 8.6753 },
  NL: { lat: 52.1326, lng: 5.2913 },
  NP: { lat: 28.3949, lng: 84.1240 },
  OM: { lat: 21.4735, lng: 55.9754 },
  PL: { lat: 51.9194, lng: 19.1451 },
  QA: { lat: 25.3548, lng: 51.1839 },
  UA: { lat: 48.3794, lng: 31.1656 },
  US: { lat: 37.0902, lng: -95.7129 },
  ZA: { lat: -30.5595, lng: 22.9375 },
};

export interface RunAnalysisKpis {
  runCount: number,
  failedCount: number,
  issueCount: number,
  agentMix: { agentType: string, count: number }[],
}

export interface RunAnalysisFilters {
  agentType: string,
  country: string,
  severity: string,
  runStatus: string,
}

export function runsInWindow(
  state: WatchTowerMockState,
  days: number,
  now = mockPulseNow(),
): WatchRun[] {
  return state.runs.filter((run) => observedInWindow(run.scheduledAt, days, now));
}

export function parseLatLng(raw: string | null | undefined): { lat: number, lng: number } | null {
  if (!raw) {
    return null;
  }
  const parts = raw.split(',').map((item) => Number(item.trim()));
  if (parts.length < 2 || !Number.isFinite(parts[0]) || !Number.isFinite(parts[1])) {
    return null;
  }
  if (parts[0] < -90 || parts[0] > 90 || parts[1] < -180 || parts[1] > 180) {
    return null;
  }
  return { lat: parts[0], lng: parts[1] };
}

export function toRunIssueCard(
  state: WatchTowerMockState,
  finding: WatchFinding,
): RunIssueCard | null {
  const run = state.runs.find((item) => item.id === finding.agentRunId);
  if (!run) {
    return null;
  }
  const company = state.watchCompanies.find((item) => item.id === finding.watchCompanyId);
  const topic = finding.watchTopicId
    ? (state.watchTopics || []).find((item) => item.id === finding.watchTopicId)
    : null;
  const payloadPoint = parseLatLng(findingPayloadString(finding, 'coordinates'));
  const point = parseLatLng(company?.addressCoordinates) || payloadPoint;
  return {
    finding,
    run,
    companyName: company?.name || topic?.title || String(finding.watchCompanyId || '—'),
    country: company?.addressCountry || findingPayloadString(finding, 'country'),
    city: company?.addressCity || findingPayloadString(finding, 'city'),
    lat: point?.lat ?? null,
    lng: point?.lng ?? null,
    industry: company?.naceSection || '',
    agentType: run.type,
    agentLabel: formatAgentType(run.type),
    targetName: resolveTargetName(
      state,
      run.watchCompanyId,
      run.watchTargetKind,
      run.watchTargetId,
    ),
  };
}

export function runIssueCardsForFindings(
  state: WatchTowerMockState,
  findings: WatchFinding[],
): RunIssueCard[] {
  return findings
    .map((finding) => toRunIssueCard(state, finding))
    .filter((card): card is RunIssueCard => Boolean(card));
}

function cityCoordKey(country: string, city: string): string {
  return `${country.trim().toUpperCase()}|${city.trim().toLowerCase()}`;
}

export function resolveIssueMapPoint(card: RunIssueCard): { lat: number, lng: number } | null {
  if (card.lat != null && card.lng != null) {
    return { lat: card.lat, lng: card.lng };
  }
  const city = CITY_COORDS[cityCoordKey(card.country, card.city)];
  if (city) {
    return city;
  }
  const country = COUNTRY_COORDS[card.country.trim().toUpperCase()];
  return country || null;
}

export function issueMapMarkers(cards: RunIssueCard[]): IssueMapMarker[] {
  const groups = new Map<string, RunIssueCard[]>();
  cards.forEach((card) => {
    const point = resolveIssueMapPoint(card);
    if (!point) {
      return;
    }
    const city = card.city || card.country;
    const key = `${card.country}|${city}|${point.lat.toFixed(4)}|${point.lng.toFixed(4)}`;
    const list = groups.get(key) || [];
    list.push(card);
    groups.set(key, list);
  });
  return Array.from(groups.entries())
    .map(([key, group]) => {
      const first = group[0];
      const point = resolveIssueMapPoint(first);
      if (!point) {
        return null;
      }
      const maxSeverity = group.reduce((best, card) => (
        severityRank(card.finding.severity) < severityRank(best)
          ? card.finding.severity
          : best
      ), group[0].finding.severity);
      return {
        key,
        country: first.country,
        city: first.city,
        lat: point.lat,
        lng: point.lng,
        count: group.length,
        maxSeverity,
        cards: group.slice().sort(
          (left, right) => right.finding.observedAt.localeCompare(left.finding.observedAt),
        ),
      };
    })
    .filter((marker): marker is IssueMapMarker => Boolean(marker))
    .sort((left, right) => right.count - left.count || left.country.localeCompare(right.country));
}

export function formatIssueAgeParts(
  iso: string,
  now = mockPulseNow(),
): { unit: IssueAgeUnit, count: number } {
  const ms = now.getTime() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 60_000) {
    return { unit: 'now', count: 0 };
  }
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) {
    return { unit: 'minutes', count: minutes };
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 48) {
    return { unit: 'hours', count: hours };
  }
  return { unit: 'days', count: Math.floor(hours / 24) };
}

export function runIssueCardsInWindow(
  state: WatchTowerMockState,
  days: number,
  now = mockPulseNow(),
): RunIssueCard[] {
  const runIds = new Set(runsInWindow(state, days, now).map((run) => run.id));
  return state.findings
    .filter((finding) => runIds.has(finding.agentRunId))
    .map((finding) => toRunIssueCard(state, finding))
    .filter((card): card is RunIssueCard => Boolean(card))
    .sort((left, right) => {
      const byRun = right.run.scheduledAt.localeCompare(left.run.scheduledAt);
      if (byRun) {
        return byRun;
      }
      return severityRank(left.finding.severity) - severityRank(right.finding.severity);
    });
}

export function runAnalysisKpis(
  runs: WatchRun[],
  cards: RunIssueCard[],
): RunAnalysisKpis {
  const mix = new Map<string, number>();
  cards.forEach((card) => {
    mix.set(card.agentType, (mix.get(card.agentType) || 0) + 1);
  });
  return {
    runCount: runs.length,
    failedCount: runs.filter((run) => run.status === 'failed').length,
    issueCount: cards.length,
    agentMix: Array.from(mix.entries())
      .map(([agentType, count]) => ({ agentType, count }))
      .sort((left, right) => left.agentType.localeCompare(right.agentType)),
  };
}

export function runIssueHeatCounts(cards: RunIssueCard[]): Record<string, number> {
  return cards.reduce<Record<string, number>>((counts, card) => {
    const key = `${card.country}|${card.agentType}`;
    return { ...counts, [key]: (counts[key] || 0) + 1 };
  }, {});
}

export function runIssueHeatCountries(cards: RunIssueCard[]): string[] {
  return Array.from(new Set(cards.map((card) => card.country).filter(Boolean))).sort();
}

export function filterRunIssueCards(
  cards: RunIssueCard[],
  filters: RunAnalysisFilters,
): RunIssueCard[] {
  return cards.filter((card) => {
    if (filters.agentType && card.agentType !== filters.agentType) {
      return false;
    }
    if (filters.country && card.country !== filters.country) {
      return false;
    }
    if (filters.severity && card.finding.severity !== filters.severity) {
      return false;
    }
    if (filters.runStatus && card.run.status !== filters.runStatus) {
      return false;
    }
    return true;
  });
}

export function runAnalysisFilterOptions(cards: RunIssueCard[]): {
  countries: string[],
  severities: string[],
} {
  return {
    countries: Array.from(new Set(cards.map((card) => card.country).filter(Boolean))).sort(),
    severities: Array.from(new Set(cards.map((card) => card.finding.severity))).sort(
      (left, right) => severityRank(left) - severityRank(right),
    ),
  };
}

export function utcDay(iso: string): string {
  return iso.slice(0, 10);
}

export function addUtcDays(day: string, delta: number): string {
  const stamp = Date.UTC(
    Number(day.slice(0, 4)),
    Number(day.slice(5, 7)) - 1,
    Number(day.slice(8, 10)) + delta,
  );
  return new Date(stamp).toISOString().slice(0, 10);
}

export function listUtcDays(startDay: string, endDay: string): string[] {
  if (!startDay || !endDay || startDay > endDay) {
    return [];
  }
  const days: string[] = [];
  let cursor = startDay;
  while (cursor <= endDay) {
    days.push(cursor);
    cursor = addUtcDays(cursor, 1);
    if (days.length > 400) {
      break;
    }
  }
  return days;
}

export function timelineDaysEndingAt(now: Date, spanDays: number): string[] {
  const endDay = now.toISOString().slice(0, 10);
  const startDay = addUtcDays(endDay, -(Math.max(spanDays, 1) - 1));
  return listUtcDays(startDay, endDay);
}

export function issueFoundDay(card: RunIssueCard): string {
  return utcDay(card.finding.observedAt);
}

export function filterCardsByFoundDays(
  cards: RunIssueCard[],
  startDay: string,
  endDay: string,
): RunIssueCard[] {
  return cards.filter((card) => {
    const day = issueFoundDay(card);
    return day >= startDay && day <= endDay;
  });
}

export function uniqueRunsFromCards(cards: RunIssueCard[]): WatchRun[] {
  const seen = new Map<number, WatchRun>();
  cards.forEach((card) => {
    seen.set(card.run.id, card.run);
  });
  return Array.from(seen.values());
}

export function issueMarkDays(cards: RunIssueCard[]): string[] {
  return Array.from(new Set(cards.map(issueFoundDay))).sort();
}

export function issueMarkCounts(cards: RunIssueCard[]): Record<string, number> {
  return cards.reduce<Record<string, number>>((counts, card) => {
    const day = issueFoundDay(card);
    return { ...counts, [day]: (counts[day] || 0) + 1 };
  }, {});
}

export const SEVERITY_HEAT_ROWS = ['critical', 'high', 'medium', 'low'] as const;

export type IssueTrendGrain = 'day' | 'week' | 'month';

export type IssueTrendSeverity = (typeof SEVERITY_HEAT_ROWS)[number];

export interface IssueTrendSeries {
  buckets: string[],
  counts: Record<IssueTrendSeverity, number[]>,
}

export function startOfUtcWeek(day: string): string {
  const year = Number(day.slice(0, 4));
  const month = Number(day.slice(5, 7)) - 1;
  const date = Number(day.slice(8, 10));
  const weekday = new Date(Date.UTC(year, month, date)).getUTCDay();
  const offset = weekday === 0 ? -6 : 1 - weekday;
  return addUtcDays(day, offset);
}

export function trendBucketKey(day: string, grain: IssueTrendGrain): string {
  if (grain === 'week') {
    return startOfUtcWeek(day);
  }
  if (grain === 'month') {
    return day.slice(0, 7);
  }
  return day;
}

export function listTrendBuckets(
  startDay: string,
  endDay: string,
  grain: IssueTrendGrain,
): string[] {
  const days = listUtcDays(startDay, endDay);
  const buckets: string[] = [];
  const seen = new Set<string>();
  days.forEach((day) => {
    const key = trendBucketKey(day, grain);
    if (!seen.has(key)) {
      seen.add(key);
      buckets.push(key);
    }
  });
  return buckets;
}

function emptyTrendCounts(length: number): Record<IssueTrendSeverity, number[]> {
  return {
    critical: Array.from({ length }, () => 0),
    high: Array.from({ length }, () => 0),
    medium: Array.from({ length }, () => 0),
    low: Array.from({ length }, () => 0),
  };
}

export function issueTrendBySeverity(
  cards: RunIssueCard[],
  startDay: string,
  endDay: string,
  grain: IssueTrendGrain,
): IssueTrendSeries {
  const buckets = listTrendBuckets(startDay, endDay, grain);
  const index = new Map(buckets.map((key, position) => [key, position]));
  const counts = emptyTrendCounts(buckets.length);
  cards.forEach((card) => {
    const day = issueFoundDay(card);
    if (day < startDay || day > endDay) {
      return;
    }
    const position = index.get(trendBucketKey(day, grain));
    if (position === undefined) {
      return;
    }
    const { severity } = card.finding;
    if (
      severity === 'critical'
      || severity === 'high'
      || severity === 'medium'
      || severity === 'low'
    ) {
      counts[severity][position] += 1;
    }
  });
  return { buckets, counts };
}

export function severityHeatRows(cards: RunIssueCard[]): string[] {
  const present = new Set(cards.map((card) => card.finding.severity));
  return SEVERITY_HEAT_ROWS.filter((item) => present.has(item));
}

export function severityAgentCounts(cards: RunIssueCard[]): Record<string, number> {
  return cards.reduce<Record<string, number>>((counts, card) => {
    const key = `${card.finding.severity}|${card.agentType}`;
    return { ...counts, [key]: (counts[key] || 0) + 1 };
  }, {});
}

export function formatUtcDayShort(day: string): string {
  return day.slice(5);
}

export function fullTimelineRange(days: string[]): TimelineRange {
  return [0, Math.max(days.length - 1, 0)];
}

export function clampTimelineRange(range: TimelineRange, days: string[]): TimelineRange {
  const max = Math.max(days.length - 1, 0);
  const start = Math.min(Math.max(range[0], 0), max);
  const end = Math.min(Math.max(range[1], start), max);
  return [start, end];
}

export function isFullTimelineRange(range: TimelineRange, days: string[]): boolean {
  const full = fullTimelineRange(days);
  return range[0] === full[0] && range[1] === full[1];
}
